import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertTriangle, Bell, X, ArrowRight, Pencil, Eye, Link2 } from "lucide-react";
import { motion } from "framer-motion";

const STATUS_LABELS = { new: "New", contacted: "Contacted", booked: "Booked", qualified: "Qualified", proposal: "Proposal", closed_won: "Won", closed_lost: "Lost" };
const getStatusColor = (s) => {
  const m = { new: "bg-blue-50 text-blue-700", contacted: "bg-yellow-50 text-yellow-700", booked: "bg-indigo-50 text-indigo-700", qualified: "bg-emerald-50 text-emerald-700", proposal: "bg-purple-50 text-purple-700", closed_won: "bg-green-100 text-green-800", closed_lost: "bg-red-50 text-red-700" };
  return m[s] || "bg-slate-50 text-slate-700";
};

export function DecayAlerts({ alerts, openLead, dismissAlert }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6" data-testid="decay-alerts">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-red-500" />
        <h2 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Deal Decay Alerts</h2>
        <span className="text-xs font-['JetBrains_Mono'] bg-red-50 text-red-700 px-1.5 py-0.5">{alerts.length}</span>
      </div>
      <div className="space-y-2">
        {alerts.slice(0, 5).map(a => (
          <div key={a.lead_id} className={`bg-white border p-4 flex items-center gap-4 ${a.severity === "critical" ? "border-red-300 bg-red-50/30" : a.severity === "warning" ? "border-amber-200" : "border-slate-200"}`} data-testid={`alert-${a.lead_id}`}>
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${a.severity === "critical" ? "text-red-500" : "text-amber-500"}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-['IBM_Plex_Sans'] font-medium text-[#0F172A]">{a.name}</span>
                <span className="text-xs text-slate-400">{a.company}</span>
                <span className={`text-xs font-['JetBrains_Mono'] px-1.5 py-0.5 ${getStatusColor(a.status)}`}>{STATUS_LABELS[a.status]}</span>
              </div>
              <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mt-0.5">
                <span className="font-medium text-red-600">{a.days_idle} days idle</span> &middot; Score: {a.deal_score} &middot; {a.suggested_action}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" onClick={() => openLead(a.lead_id)} className="bg-[#0033CC] text-white rounded-none h-7 text-xs font-['Outfit']" data-testid={`alert-action-${a.lead_id}`}>Open</Button>
              <button onClick={() => dismissAlert(a.lead_id)} className="text-slate-400 hover:text-slate-600" data-testid={`alert-dismiss-${a.lead_id}`}><X className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export function BookingsTable({ bookings, startReschedule, cancelBooking }) {
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Name</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Email</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Date</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Time</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Status</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {bookings.map(b => (
            <TableRow key={b.booking_id} className="hover:bg-slate-50">
              <TableCell className="font-['IBM_Plex_Sans'] text-sm">{b.name}</TableCell>
              <TableCell className="font-['IBM_Plex_Sans'] text-sm">{b.email}</TableCell>
              <TableCell className="font-['JetBrains_Mono'] text-sm">{b.date}</TableCell>
              <TableCell className="font-['JetBrains_Mono'] text-sm">{b.time_slot}</TableCell>
              <TableCell><span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${b.status === "confirmed" ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500"}`}>{b.status}</span></TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => startReschedule(b)} className="text-[#0033CC] h-7 text-xs" data-testid={`reschedule-${b.booking_id}`}><Pencil className="w-3 h-3 mr-1" />Reschedule</Button>
                  <Button variant="ghost" size="sm" onClick={() => cancelBooking(b.booking_id)} className="text-red-500 h-7 text-xs" data-testid={`cancel-booking-${b.booking_id}`}><X className="w-3 h-3 mr-1" />Cancel</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {bookings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No bookings</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

export function SubmissionsTable({ submissions, loadSubDetail, openLead, formatStatus }) {
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Company</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Status</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Payment</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Score</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Lead</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider">Date</TableHead>
          <TableHead className="font-['Outfit'] text-xs uppercase tracking-wider"></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {submissions.map(sub => (
            <TableRow key={sub.audit_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => loadSubDetail(sub.audit_id)} data-testid={`sub-row-${sub.audit_id}`}>
              <TableCell className="font-['IBM_Plex_Sans'] text-sm">
                <div className="font-medium text-[#0F172A]">{sub.company_info?.company_name || "—"}</div>
                <div className="text-xs text-slate-400">{sub.user_email}</div>
              </TableCell>
              <TableCell><span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${sub.status === "report_generated" ? "bg-green-50 text-green-700" : sub.status === "completed" ? "bg-blue-50 text-blue-700" : sub.status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-500"}`}>{formatStatus(sub.status)}</span></TableCell>
              <TableCell><span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${sub.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500"}`}>{formatStatus(sub.payment_status)}</span></TableCell>
              <TableCell className="font-['JetBrains_Mono'] text-sm">{sub.scores?.overall != null ? <span className="text-[#0033CC] font-medium">{sub.scores.overall}</span> : "—"}</TableCell>
              <TableCell>
                {sub.linked_lead_id ? (
                  <button onClick={(e) => { e.stopPropagation(); openLead(sub.linked_lead_id); }} className="text-xs font-['JetBrains_Mono'] text-[#0033CC] flex items-center gap-1 hover:underline" data-testid={`sub-lead-link-${sub.audit_id}`}>
                    <Link2 className="w-3 h-3" /> View Lead
                  </button>
                ) : <span className="text-xs text-slate-300">—</span>}
              </TableCell>
              <TableCell className="text-xs text-slate-400">{sub.created_at?.split("T")[0] || "—"}</TableCell>
              <TableCell><Eye className="w-4 h-4 text-slate-300" /></TableCell>
            </TableRow>
          ))}
          {submissions.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-8">No submissions</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}
